import type { Id } from 'convex/_generated/dataModel';
import type { Garden } from '@modules/garden-planner/business_logic';

export function getPlantsInCell(plantedPlants: Garden['plants'], row: number, col: number) {
  return plantedPlants.filter((p) => p.row === row && p.col === col);
}

export function isCellAvailable(
  availableCells: Array<{ row: number; col: number }>,
  row: number,
  col: number
) {
  return availableCells.some((cell) => cell.row === row && cell.col === col);
}

export function getNextAvailableSlot(
  plantedPlants: Garden['plants'],
  row: number,
  col: number,
  getSlotsForCell: (row: number, col: number) => number
) {
  const cellPlants = getPlantsInCell(plantedPlants, row, col);
  const slotsPerCell = getSlotsForCell(row, col);
  for (let slot = 0; slot < slotsPerCell; slot++) {
    if (!cellPlants.find((p) => p.slot === slot)) {
      return slot;
    }
  }
  return null;
}

export function createHandleCellClick(
  garden: Garden | null | undefined,
  availableCells: Array<{ row: number; col: number }>,
  selectedPlant: string | null,
  isAvailabilityMode: boolean,
  isClearMode: boolean,
  isCellSettingsMode: boolean,
  getSlotsForCell: (row: number, col: number) => number,
  onCellSelect: (cell: { row: number; col: number }) => void,
  updateGarden: (args: {
    gardenId: Id<'gardens'>;
    plants: Array<{ row: number; col: number; slot: number; plantId: string }>;
  }) => Promise<unknown>,
  toggleCellAvailability: (args: {
    gardenId: Id<'gardens'>;
    row: number;
    col: number;
  }) => Promise<unknown>,
  isBulkEditMode = false,
  onBulkCellToggle: (row: number, col: number) => void = () => {}
) {
  return async (row: number, col: number, _event?: unknown) => {
    if (!garden) return;

    if (isAvailabilityMode) {
      await toggleCellAvailability({ gardenId: garden._id, row, col });
      return;
    }

    if (isClearMode) {
      const nextPlants = garden.plants.filter((plant) => !(plant.row === row && plant.col === col));
      await updateGarden({ gardenId: garden._id, plants: nextPlants });
      return;
    }

    if (isCellSettingsMode) {
      if (isBulkEditMode) {
        if (!isCellAvailable(availableCells, row, col)) return;
        onBulkCellToggle(row, col);
        return;
      }
      onCellSelect({ row, col });
      return;
    }

    if (!isCellAvailable(availableCells, row, col)) {
      return;
    }

    if (selectedPlant) {
      const nextSlot = getNextAvailableSlot(garden.plants, row, col, getSlotsForCell);
      if (nextSlot !== null) {
        const nextPlants = [...garden.plants, { row, col, slot: nextSlot, plantId: selectedPlant }];
        await updateGarden({ gardenId: garden._id, plants: nextPlants });
      }
    }
  };
}
